import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../supabase';
import { setMonitoringOrg } from '../utils/monitoring';
import { resolveRole } from '../utils/roleAuthority';
import { resolveUiMode, writeStoredUiMode, canToggleUiMode, UI_MODE_PRO } from '../utils/uiMode';
import { isProtectedOrg } from '../utils/protectedOrgs';

const TenantContext = createContext();

// ─── Política del plan trial ──────────────────────────────────────────────────
// El trial NO desbloquea features en bloque: enseña el mismo set que el plan que
// se le quiere vender. Al leerlo de datos, una feature nueva que nadie declaró
// en TRIAL_PREVIEW_PLAN queda en false también en trial (fail-closed).
const TRIAL_PREVIEW_PLAN = 'pro';

// Excepción al set heredado: whatsapp_agent requiere setup externo (sesión/QR en
// Railway) y por decisión de producto NO se prueba en trial. Se fuerza a false
// aunque el plan de referencia lo declare true.
// Ver progress/trial-whatsapp-agent/hallazgos.md.
const TRIAL_EXCLUDED_FEATURES = ['whatsapp_agent'];

export const useTenant = () => {
    const context = useContext(TenantContext);
    if (!context) throw new Error('useTenant must be used within a TenantProvider');
    return context;
};

export const TenantProvider = ({ children }) => {
    const [organizationId, setOrganizationId] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [allOrganizations, setAllOrganizations] = useState([]); // multi-sucursal
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [brandingSettings, setBrandingSettings] = useState({});
    const [isBusinessOpen, setIsBusinessOpen] = useState(false);
    const [planFeatures, setPlanFeatures] = useState({});
    const [planAiAgent, setPlanAiAgent] = useState(false);
    const [planLimits, setPlanLimits] = useState({ maxEmployees: null, maxCustomers: null });
    // Orgs protegidas leídas de la BD. Solo AÑADEN a la constante de respaldo;
    // ver src/utils/protectedOrgs.js para por qué la unión y no la intersección.
    const [protectedOrgIds, setProtectedOrgIds] = useState([]);

    const toggleBusinessStatus = useCallback(async (forcedStatus) => {
        if (!organizationId) return;

        // If forcedStatus is not a boolean (e.g. called from a button click event), toggle current state
        const nextStatus = typeof forcedStatus === 'boolean' ? forcedStatus : !isBusinessOpen;

        // Actualización optimista: cambia UI inmediatamente
        setIsBusinessOpen(nextStatus);

        const { error } = await supabase
            .from('business_settings')
            .upsert({
                organization_id: organizationId,
                setting_key: 'is_open',
                setting_value: nextStatus ? 'true' : 'false'
            }, { onConflict: 'organization_id,setting_key' });

        if (error) {
            // Rollback: revertir al estado anterior si falló
            console.error("Error toggling business status:", error);
            setIsBusinessOpen(!nextStatus);
            alert("Error al cambiar el estado del negocio. Verifica tu conexión e intenta de nuevo.");
        }
    }, [organizationId, isBusinessOpen]);

    const loadTenantInfo = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                // Cambio 1: limpiar estado si el usuario cambió (ej: signUp de nuevo negocio
                // mientras hay sesión activa — signUp emite SIGNED_IN directo sin SIGNED_OUT)
                if (userProfile?.id && userProfile.id !== user.id) {
                    setOrganizationId(null);
                    setOrganization(null);
                    setUserProfile(null);
                    setAllOrganizations([]);
                    setBrandingSettings({});
                    // Las features del plan del usuario anterior no deben sobrevivir el
                    // cambio de cuenta (un owner trial heredaba whatsapp_agent de Express)
                    setPlanFeatures({});
                    setPlanAiAgent(false);
                }

                let currentOrgId = organizationId;

                // Org efectiva + rol efectivo + membresías, calculados por la BD
                // con LA MISMA autoridad que la RLS (get_session_context →
                // current_org_id/current_user_role). El front no re-deriva nada:
                // si la RLS va a enseñar la org B, aquí llega la org B. Sin este
                // paso, tras un switch el front filtraría por la org del perfil
                // mientras la RLS sirve la activa → todas las pantallas vacías.
                // Fallback null (RPC ausente/error) → comportamiento actual.
                const { data: sessionCtx } = await supabase.rpc('get_session_context');
                const activeOrgId = sessionCtx?.organization_id || null;
                const serverRole  = sessionCtx?.role || null;
                const memberships = Array.isArray(sessionCtx?.memberships) ? sessionCtx.memberships : [];

                const { data: profile } = await supabase
                    .from('user_profiles')
                    .select('*, organizations(*)')
                    .eq('id', user.id)
                    .single();

                if (profile) {
                    currentOrgId = activeOrgId || profile.organization_id;
                    setOrganizationId(currentOrgId);
                    // El rol de la org activa lo decide la BD (superadmin global >
                    // membresía > legacy). NO recomputar con máximo privilegio aquí:
                    // anularía el rol-por-org (owner en A seguiría owner en B).
                    setUserProfile({ ...profile, role: serverRole || profile.role });

                    // Cargar planes y features (SaaS logic) — de la org ACTIVA
                    const { data: orgData } = await supabase
                        .from('organizations')
                        .select('*')
                        .eq('id', currentOrgId)
                        .single();

                    // Reset INCONDICIONAL antes del branch: una org sin plan no
                    // debe heredar las features de la org anterior (familia del
                    // bug "planFeatures rancio", PR #62 — ahora también aplica
                    // entre sucursales del mismo usuario).
                    setPlanFeatures({});
                    setPlanAiAgent(false);
                    setPlanLimits({ maxEmployees: null, maxCustomers: null });

                    if (orgData) {
                        setOrganization(orgData);
                        setMonitoringOrg(orgData.id, orgData.name);

                        // Multi-sucursal: la lista sale de organization_members —
                        // JAMÁS de is_member_of/set_active_org (regla vinculante:
                        // un superadmin pasa is_member_of para TODA org de la
                        // plataforma; si la lista saliera de ahí, este menú le
                        // enseñaría los car wash de los clientes). Shape {id,
                        // name, status, plan} preservado: lo consumen Layout y
                        // resolveBusinessName.
                        let ownerOrgs = memberships
                            .map(m => ({ id: m.id, name: m.name, status: m.status, plan: m.plan }))
                            .filter(o => o.id && o.status !== 'inactive');
                        // La org activa siempre presente aunque no tenga membresía
                        // (la org del perfil, o un superadmin impersonando)
                        if (!ownerOrgs.some(o => o.id === orgData.id)) {
                            ownerOrgs = [{ id: orgData.id, name: orgData.name, status: orgData.status, plan: orgData.plan }, ...ownerOrgs];
                        }
                        if (ownerOrgs.length <= 1) {
                            // Fallback legacy por owner_email (usuarios sin
                            // membresías: comportamiento actual intacto)
                            const { data: legacyOrgs } = await supabase
                                .from('organizations')
                                .select('id, name, status, plan')
                                .eq('owner_email', orgData.owner_email)
                                .neq('status', 'inactive');
                            if (legacyOrgs && legacyOrgs.length > 1) ownerOrgs = legacyOrgs;
                        }
                        setAllOrganizations(ownerOrgs.length > 1 ? ownerOrgs : []);

                        // Load plan features + límites desde saas_plans (también para trial:
                        // los límites max_employees/max_customers aplican a todos los planes)
                        if (orgData.plan) {
                            // El trial es una VISTA PREVIA del plan de referencia: sus features
                            // se heredan de TRIAL_PREVIEW_PLAN en vez de desbloquearse en bloque
                            // en hasFeature(). Así una feature que nadie declaró queda en false
                            // también en trial (fail-closed), sin duplicar la lista de Pro en
                            // una fila de datos que se desincroniza (la fila 'trial' hoy tiene
                            // todo en false — ver progress/fix-gate-fail-closed/02-implementation.md).
                            // Los LÍMITES siguen saliendo de la fila propia del plan.
                            const featuresPlanId = orgData.plan === 'trial' ? TRIAL_PREVIEW_PLAN : orgData.plan;
                            const planIds = [...new Set([orgData.plan, featuresPlanId])];
                            const { data: planRows } = await supabase
                                .from('saas_plans')
                                .select('id, features, ai_agent, max_employees, max_customers')
                                .in('id', planIds);
                            const ownPlan = planRows?.find(p => p.id === orgData.plan);
                            const featuresPlan = planRows?.find(p => p.id === featuresPlanId);
                            // Resetear SIEMPRE: omitir el setter dejaba vivas las features
                            // de la cuenta/plan anterior en trial (y con fila de plan ausente)
                            setPlanFeatures(featuresPlan?.features || {});
                            setPlanAiAgent(orgData.plan !== 'trial' && ownPlan?.ai_agent === true);
                            setPlanLimits({
                                maxEmployees: ownPlan?.max_employees ?? null,
                                maxCustomers: ownPlan?.max_customers ?? null,
                            });
                        }
                    }
                } else {
                    // FALLBACK: If no profile, check legacy employees table
                    const { data: employeeData } = await supabase
                        .from('employees')
                        .select('*, organizations(*)')
                        .eq('user_id', user.id)
                        .maybeSingle();
                    
                    if (employeeData && employeeData.organization_id) {
                        currentOrgId = activeOrgId || employeeData.organization_id;
                        setOrganizationId(currentOrgId);
                        setUserProfile({
                            id: user.id,
                            // Antes esto hardcodeaba 'employee' e IGNORABA
                            // employeeData.role, pese a haberlo traido con
                            // select('*'): un manager sin fila en user_profiles
                            // quedaba degradado a empleado en toda la UI mientras
                            // la RLS le seguia autorizando su rol real. Ahora pasa
                            // por la misma autoridad que el resto (y con org
                            // activa, la BD ya trae el rol de ESA org).
                            role: serverRole || resolveRole(employeeData.role),
                            organization_id: employeeData.organization_id,
                            full_name: employeeData.name
                        });
                        setOrganization(employeeData.organizations);
                        // Sentry etiquetaba con la org vieja en esta rama (solo se
                        // llamaba en la rama de user_profiles) — blindaje del plan.
                        if (employeeData.organizations) {
                            setMonitoringOrg(employeeData.organizations.id, employeeData.organizations.name);
                        }
                        console.log("Legacy employee linked via employees table:", employeeData.id);
                    }
                }

                // Common organization details (if we have an ID but not full object yet)
                if (currentOrgId && !organization) {
                    const { data: finalOrg } = await supabase
                        .from('organizations')
                        .select('*')
                        .eq('id', currentOrgId)
                        .single();
                    if (finalOrg) setOrganization(finalOrg);
                }

                if (currentOrgId) {
                    // Cargar todos los settings desde business_settings (fuente única)
                    const [{ data: bSettings }, { data: legacyData }] = await Promise.all([
                        supabase.from('business_settings').select('*').eq('organization_id', currentOrgId),
                        // Leer también tabla legacy 'settings' para retrocompatibilidad
                        supabase.from('settings').select('key, value').eq('organization_id', currentOrgId),
                    ]);

                    // Primero volcar la tabla legacy (prioridad más baja)
                    const merged = {};
                    if (legacyData) legacyData.forEach(s => { merged[s.key] = s.value; });

                    // Luego business_settings sobreescribe (prioridad más alta)
                    if (bSettings) {
                        bSettings.forEach(s => { merged[s.setting_key] = s.setting_value; });
                        // Sin guard de undefined: una org sin la key is_open queda
                        // CERRADA por defecto, no hereda el estado de la anterior
                        // (blindaje del plan para cualquier refresh entre orgs).
                        setIsBusinessOpen(merged.is_open === 'true');
                    }

                    // Garantizar claves críticas
                    if (!merged.ath_mobile_number) merged.ath_mobile_number = '';
                    if (!merged.ath_link) merged.ath_link = '';
                    if (!merged.business_address) merged.business_address = '';

                    setBrandingSettings(merged);
                }
            }
        } catch (error) {
            console.error("Error crítico en TenantContext:", error);
        } finally {
            setLoading(false);
        }
    };

    // ── Organizaciones protegidas (exentas del muro de trial) ────────────────
    // EFECTO APARTE, y no dentro de loadTenantInfo, por dos razones:
    //   1. Aislamiento de fallos. El catch de loadTenantInfo es de brocha gorda
    //      (solo console.error) y aborta lo que quede del load: meter aquí una
    //      lectura a una tabla nueva acoplaría el login de TODOS los inquilinos
    //      a ella. Aquí, si falla, no se entera nadie más.
    //   2. No hay parpadeo del muro que temer: la exención de la org demo la da
    //      la CONSTANTE, que es síncrona. Esta lectura solo puede AÑADIR orgs
    //      que un superadmin diera de alta después, y para esas un instante de
    //      muro es aceptable.
    // ⚠️ ESPERA A QUE HAYA SESIÓN (corregido 2026-08-05). Con `[]` de
    //    dependencias esto corría UNA vez al montar — antes de que el cliente
    //    de Supabase terminara de restaurar la sesión de localStorage. La
    //    petición salía entonces como `anon`, y `anon` NO tiene privilegio de
    //    tabla aquí a propósito (la migración hace REVOKE a anon y GRANT
    //    SELECT solo a authenticated). Resultado: "permission denied for table
    //    platform_protected_orgs" en cada arranque, la lectura no se
    //    reintentaba nunca, y la exención quedaba viviendo SOLO de la
    //    constante de respaldo — perdiendo justo lo que la tabla existe para
    //    dar: añadir organizaciones protegidas SIN desplegar.
    //
    //    La base estaba bien (verificado en prod: relacl authenticated=r,
    //    política SELECT USING(true), y una sonda con SET LOCAL role
    //    authenticated lee las filas). Era una carrera del cliente, no un
    //    permiso que faltara.
    //
    //    `organizationId` como disparador: solo se rellena cuando
    //    loadTenantInfo() resolvió con un usuario de verdad, así que es la
    //    señal más barata de "ya hay sesión". Sigue siendo aditivo: si esta
    //    lectura falla, la constante cubre las dos demos de Apple.
    useEffect(() => {
        if (!organizationId) return;   // sin sesión todavía: se reintenta al llegar
        let cancelado = false;
        (async () => {
            try {
                const { data, error } = await supabase
                    .from('platform_protected_orgs')
                    .select('organization_id');
                if (error) throw error;
                if (!cancelado && data) setProtectedOrgIds(data.map(r => r.organization_id));
            } catch (e) {
                // Silencioso A PROPÓSITO: sin esta tabla la constante de respaldo
                // ya cubre lo que no puede fallar. Se deja rastro para depurar.
                console.warn('[TenantContext] platform_protected_orgs no disponible; se usa solo el respaldo local.', e?.message);
            }
        })();
        return () => { cancelado = true; };
    }, [organizationId]);

    // ── Switch de sucursal (org activa) ───────────────────────────────────────
    // set_active_org valida la membresía en el servidor; la recarga DURA es
    // load-bearing: ~30 estados del dashboard, drafts del modal de venta y
    // settings con setters condicionales se limpian de golpe. Limpieza SOLO por
    // claves enumeradas — JAMÁS localStorage.clear() (mataría la sesión de
    // Supabase y cw_portal_session del portal del cliente).
    const switchOrg = useCallback(async (orgId) => {
        if (!orgId || orgId === organizationId) return;
        const { error } = await supabase.rpc('set_active_org', { p_org: orgId });
        if (error) {
            alert('No se pudo cambiar de sucursal: ' + error.message);
            return;
        }
        Object.keys(localStorage)
            .filter(k => k.startsWith('manual_cars_'))
            .forEach(k => localStorage.removeItem(k));
        localStorage.removeItem('n8n_webhook_url');
        // sessionStorage SOBREVIVE al reload — limpiar explícito
        sessionStorage.removeItem('hasSkippedOnboarding');
        window.location.assign('/dashboard');
    }, [organizationId]);

    // Guard multi-pestaña: la org activa es por USUARIO, no por pestaña. Si otra
    // pestaña/dispositivo conmutó, esta sesión quedaría con el front en la org
    // vieja y la RLS en la nueva (listas vacías en silencio). Al recuperar foco,
    // se compara la autoridad del servidor contra la local y se recarga si difieren.
    useEffect(() => {
        const onFocus = async () => {
            if (!organizationId) return;
            const { data: serverOrg } = await supabase.rpc('current_org_id');
            if (serverOrg && serverOrg !== organizationId) window.location.reload();
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [organizationId]);

    useEffect(() => {
        loadTenantInfo();

        // 1. AUTO-REFRESH ON SUCCESSFUL PAYMENT
        const params = new URLSearchParams(window.location.search);
        if (params.get('success')) {
            console.log("Pago detectado en URL, recargando tenant...");
            setTimeout(loadTenantInfo, 2500); 
        }

        // 2. AUTH STATE SYNC
        const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event) => {
            if (event === 'SIGNED_OUT') {
                // Logout real (signOut()): limpiar estado inmediatamente
                setOrganizationId(null);
                setOrganization(null);
                setUserProfile(null);
                setAllOrganizations([]);
                setBrandingSettings({});
                // Sanear el webhook n8n viejo pegado en este navegador (contaminaba otra org).
                localStorage.removeItem('n8n_webhook_url');
                setLoading(false);
            } else {
                // SIGNED_IN, TOKEN_REFRESHED, USER_UPDATED, INITIAL_SESSION → cargar normal
                setTimeout(() => loadTenantInfo(), 0);
            }
        });

        return () => {
            authSub.unsubscribe();
        };
    }, []);

    // 3. REAL-TIME BUSINESS STATUS SYNC (Separated to handle organizationId changes correctly)
    useEffect(() => {
        if (!organizationId) return;

        const channelBs = supabase
            .channel(`public:business_settings_${organizationId}`)
            .on('postgres_changes', { 
                event: '*', 
                schema: 'public', 
                table: 'business_settings',
                filter: `organization_id=eq.${organizationId}`
            }, (payload) => {
                const updatedRow = payload.new || payload.old;
                if (!updatedRow) return;
                if (updatedRow.setting_key === 'is_open') {
                    setIsBusinessOpen(updatedRow.setting_value === 'true');
                }
                // Propagar cualquier cambio a brandingSettings en tiempo real
                setBrandingSettings(prev => ({
                    ...prev,
                    [updatedRow.setting_key]: updatedRow.setting_value,
                }));
            })
            .subscribe((status) => {
                console.log(`Supabase Realtime Status for org ${organizationId}:`, status);
            });

        return () => {
            supabase.removeChannel(channelBs);
        };
    }, [organizationId]);

    useEffect(() => {
        const defaultIcon = '/icon-1024.png';
        const appName = organization?.name || 'CarWash Pro';
        const appLogo = organization?.logo_url || brandingSettings?.organization_logo || defaultIcon;

        document.title = `${appName} - Sistema de Gestión`;

        const setLink = (rel, href, type) => {
            let link = document.querySelector(`link[rel="${rel}"]`);
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', rel);
                document.head.appendChild(link);
            }
            link.setAttribute('href', href);
            if (type) link.setAttribute('type', type);
        };

        setLink('icon', appLogo, 'image/png');
        setLink('apple-touch-icon', appLogo);

        const manifest = {
            name: appName,
            short_name: appName.length > 12 ? appName.slice(0, 12) : appName,
            description: `Portal de ${appName}`,
            theme_color: '#0f172a',
            background_color: '#0f172a',
            display: 'standalone',
            orientation: 'portrait',
            // start_url '/' (no '/dashboard'): la PWA la instalan TANTO el negocio
            // como el CLIENTE (portal). SmartRoot ("/") enruta a cada quien —
            // negocio→/dashboard, cliente→su /portal/t/<token> por el token guardado.
            // Con '/dashboard' el cliente caía en RequireAuth→/login (pantalla ajena).
            start_url: '/',
            icons: [
                { src: appLogo, sizes: '1024x1024', type: 'image/png', purpose: 'any' },
                { src: appLogo, sizes: '1024x1024', type: 'image/png', purpose: 'maskable' }
            ]
        };

        const manifestBlob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
        const manifestUrl = URL.createObjectURL(manifestBlob);
        setLink('manifest', manifestUrl);

        return () => URL.revokeObjectURL(manifestUrl);
    }, [organization?.name, organization?.logo_url, brandingSettings?.organization_logo]);

    // ── Autoridad única de rol ────────────────────────────────────────────────
    // Un solo valor por render, consumido por hasFeature, por los límites de plan
    // y por todo lo que lea useTenant().role. Antes cada uno leía userProfile.role
    // por su cuenta, así que un cambio en la resolución podía dejarlos
    // contradiciéndose dentro del mismo render.
    //
    // `undefined` mientras el perfil no ha cargado — NO se colapsa a 'employee'.
    // Esa distinción es carga útil: los consumidores la usan para saber que el rol
    // todavía no resolvió, en vez de tratar a un dueño como empleado durante el
    // primer render.
    const effectiveRole = userProfile ? resolveRole(userProfile.role) : undefined;

    // ── Modo de interfaz (Fácil / Pro) ────────────────────────────────────────
    // Preferencia POR PERSONA en localStorage (ver src/utils/uiMode.js). Se
    // guarda aquí solo para que haya un único valor por render; no es estado del
    // tenant. `brandingSettings.ui_mode_default` es una semilla opcional de la
    // organización para cuentas nuevas, nunca el estado activo.
    const [uiModeState, setUiModeState] = useState(UI_MODE_PRO);

    useEffect(() => {
        if (!userProfile?.id) return;
        setUiModeState(resolveUiMode({
            userId: userProfile.id,
            role: resolveRole(userProfile.role),
            createdAt: userProfile.created_at,
            orgDefault: brandingSettings.ui_mode_default,
        }));
    }, [userProfile?.id, userProfile?.role, userProfile?.created_at, brandingSettings.ui_mode_default]);

    const setUiMode = useCallback((mode) => {
        if (!canToggleUiMode(effectiveRole)) return;
        writeStoredUiMode(userProfile?.id, mode);
        setUiModeState(mode);
    }, [effectiveRole, userProfile?.id]);

    // Feature checker — FAIL-CLOSED: sin declaración explícita en datos, no hay
    // feature. Ningún plan (trial incluido) desbloquea nada "en bloque": el trial
    // hereda su set de TRIAL_PREVIEW_PLAN al cargar planFeatures, así que una
    // feature nueva que nadie declaró queda oculta en TODOS los planes.
    // Superadmin sigue viéndolo todo (es la cuenta de soporte).
    const hasFeature = (feature) => {
        if (effectiveRole === 'superadmin') return true;
        if (organization?.plan === 'trial' && TRIAL_EXCLUDED_FEATURES.includes(feature)) return false;
        return planFeatures[feature] === true;
    };

    // Compuerta del agente WhatsApp: espeja el OR del servidor
    // (whatsapp-agent/server.js getOrgInfo). Trial queda EXCLUIDO en ambos
    // lados: hasFeature lo fuerza a false (TRIAL_EXCLUDED_FEATURES) y
    // planAiAgent ya exige plan !== 'trial'.
    const hasWhatsAppAgent = hasFeature('whatsapp_agent') || planAiAgent === true;

    // "Recibos y avisos por WhatsApp" (links wa.me manuales): Pro y Business.
    // Business también tiene el agente IA, que implica los recibos manuales.
    const hasWhatsAppNotices = hasFeature('whatsapp_notices') || hasWhatsAppAgent;

    // Computed Properties for UX
    const isTrial = organization?.plan === 'trial';
    const isSuspended = organization?.status === 'suspended' || organization?.status === 'inactive';
    const trialEndsAt = organization?.trial_ends_at ? new Date(organization.trial_ends_at) : null;
    const now = new Date();
    
    const diffMs = trialEndsAt ? trialEndsAt - now : 0;
    const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    // Exención de plataforma: la organización demo del revisor de App Store no
    // cae en el muro por vencimiento. Se resuelve por UUID (constante + tabla),
    // NO por fecha: empujar trial_ends_at se rompe en la siguiente sumisión y
    // el aviso llega en forma de rechazo.
    //
    // ⚠️ NO intentar eximir poniendo trial_ends_at = NULL: con la fecha nula,
    // diffMs cae a 0 arriba, daysLeft queda en 0 y isTrialExpired sale TRUE —
    // "borrar la fecha" BLOQUEA la organización en vez de liberarla.
    const isTrialExempt = isProtectedOrg(organizationId, protectedOrgIds);

    // El bloqueo se activa si es trial Y ya pasaron los días Y no está exenta
    const isTrialExpired = isTrial && daysLeft <= 0 && !isTrialExempt;

    // Límites del plan (saas_plans.max_employees / max_customers).
    // -1 o null = ilimitado; superadmin nunca tiene límite.
    const toLimit = (v) => (effectiveRole === 'superadmin' || v == null || v < 0) ? Infinity : v;
    const maxEmployees = toLimit(planLimits.maxEmployees);
    const maxCustomers = toLimit(planLimits.maxCustomers);

    return (
        <TenantContext.Provider value={{
            organizationId,
            organization,
            allOrganizations,
            userProfile,
            loading,
            role: effectiveRole,
            isTrial,
            daysLeft,
            isTrialExpired,
            isTrialExempt,
            isSuspended,
            isBusinessOpen,
            brandingSettings,
            toggleBusinessStatus,
            planFeatures,
            hasFeature,
            hasWhatsAppAgent,
            hasWhatsAppNotices,
            maxEmployees,
            maxCustomers,
            reservationsEnabled: brandingSettings.reservations_enabled === 'true',
            uiMode: uiModeState,
            setUiMode,
            canToggleUiMode: canToggleUiMode(effectiveRole),
            switchOrg,
            refresh: loadTenantInfo
        }}>
            {children}
        </TenantContext.Provider>
    );
};
